import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listConsultationTypes, listConsultants, getConsultantAvailability, bookAppointment } from "@/lib/customer/appointments.functions";
import { createAppointmentPayment, verifyAppointmentPayment } from "@/lib/customer/appointment-payments.functions";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Calendar, Clock, User, CheckCircle2, AlertCircle } from "lucide-react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { ensureCustomerProfile, fetchCustomerProfile } from "@/lib/customer/customer-api";
import { todayInIST, formatISTDateTime, toISTDate } from "@/lib/date/ist";
import { loadRazorpayScript } from "@/lib/payments/razorpay-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/appointments")({
  head: () => ({
    meta: [
      { title: "Book a Consultation — Poonam Kapur Healthy Kitchen" },
      { name: "description", content: "Book a nutrition consultation with Poonam Kapur Healthy Kitchen." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Book a Consultation — Poonam Kapur Healthy Kitchen" },
      { property: "og:description", content: "Arrange a nutrition consultation with Poonam Kapur Healthy Kitchen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AppointmentBookingPage,
});

type Step = "type" | "consultant" | "time" | "payment" | "success";

function AppointmentBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("type");
  const [selectedType, setSelectedType] = useState<any>(null);
  const [selectedConsultant, setSelectedConsultant] = useState<any>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [isBooking, setIsBooking] = useState(false);

  const typesQuery = useQuery({
    queryKey: ["consultation-types"],
    queryFn: () => listConsultationTypes(),
  });

  const consultantsQuery = useQuery({
    queryKey: ["consultants"],
    queryFn: () => listConsultants(),
  });

  const availabilityQuery = useQuery({
    queryKey: ["consultant-availability", selectedConsultant?.id],
    queryFn: () => getConsultantAvailability({ data: { consultantId: selectedConsultant.id } }),
    enabled: !!selectedConsultant?.id && (step === "time" || step === "consultant"), // Pre-fetch to be ready
  });


  const bookFn = useServerFn(bookAppointment);
  const createPaymentFn = useServerFn(createAppointmentPayment);
  const verifyPaymentFn = useServerFn(verifyAppointmentPayment);

  // Generate slots for the next 14 days based on configured IST windows
  const availableSlots = useMemo(() => {
    if (!availabilityQuery.data || !selectedType) return [];
    
    const { availability, blocked, booked } = availabilityQuery.data;
    const slots: string[] = [];
    const now = new Date();
    const duration = selectedType.duration_minutes || 30;


    // We generate slots for the next 14 days
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      
      // Calculate IST weekday and date parts for this loop iteration
      const istDateStr = toISTDate(date);
      
      // Determine day of week in IST
      const istDayOfWeek = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getDay();





      // Find availability for this IST weekday
      const dayAvailItems = availability.filter((a: any) => a.day_of_week === istDayOfWeek);


      dayAvailItems.sort((a: any, b: any) => a.start_time.localeCompare(b.start_time));

      for (const avail of dayAvailItems) {
        // Construct the slot start/end by combining the IST date and the window's times
        // We use the date string "YYYY-MM-DD" and append the time
        // Construct IST window using explicit string format that is widely compatible
        const windowStart = new Date(`${istDateStr}T${avail.start_time.slice(0, 5)}:00+05:30`);
        const windowEnd = new Date(`${istDateStr}T${avail.end_time.slice(0, 5)}:00+05:30`);
        
        if (isNaN(windowStart.getTime())) {
          continue;
        }










        let currentTimestamp = windowStart.getTime();

        while (currentTimestamp + duration * 60000 <= windowEnd.getTime()) {
          const slotStart = new Date(currentTimestamp);
          const slotEnd = new Date(currentTimestamp + duration * 60000);
          
          if (slotStart.getTime() > now.getTime()) {
            const startISO = slotStart.toISOString();
            const endISO = slotEnd.toISOString();

            const isBlocked = blocked.some((b: any) => 
              (startISO >= b.start_time && startISO < b.end_time) ||
              (endISO > b.start_time && endISO <= b.end_time)
            );

            const isBooked = booked.some((b: any) => 
              (startISO >= b.start_time && startISO < b.end_time) ||
              (endISO > b.start_time && endISO <= b.end_time)
            );

            if (!isBlocked && !isBooked) {
              slots.push(startISO);
            }
          }
          currentTimestamp += duration * 60000;
          if (slots.length > 50) break;
        }
        if (slots.length > 50) break;
      }
    }
    return slots;
  }, [availabilityQuery.data, selectedType]);


  const handleBooking = async () => {
    toast.error("Consultation payments are temporarily unavailable. Please contact us to arrange your consultation.");
  };

  const isLoading = typesQuery.isLoading || consultantsQuery.isLoading;

  if (step === "success") {
    return (
      <SiteLayout>
        <Section className="py-20">
          <Container className="max-w-md text-center">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
            </div>
            <h1 className="mb-2 text-3xl font-bold">Booking Confirmed!</h1>
            <p className="mb-8 text-muted-foreground">
              Your appointment has been successfully scheduled and paid. You will receive a confirmation email with the meeting details shortly.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate({ to: "/account/appointments" } as any)}>
                View My Appointments
              </Button>
              <Button variant="outline" onClick={() => navigate({ to: "/" })}>
                Back to Home
              </Button>
            </div>
          </Container>
        </Section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Health Consultation"
        title="Book your health and nutrition appointment."
        intro="Professional guidance for your wellness journey."
      />

      <Section>
        <Container>
          {isLoading ? (
            <div className="flex justify-center p-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-12 lg:grid-cols-3">
              <div className="lg:col-span-2">
                {step === "type" && (
                  <div>
                    <h3 className="mb-6 text-2xl font-semibold">Select Consultation Type</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      {(typesQuery.data || []).map((type) => (
                        <Card 
                          key={type.id} 
                          className={cn(
                            "cursor-pointer transition-all hover:border-primary/50",
                            selectedType?.id === type.id && "border-primary bg-primary/5 shadow-md"
                          )}
                          onClick={() => {
                            setSelectedType(type);
                            setStep("consultant");
                          }}
                        >
                          <CardHeader>
                            <CardTitle className="text-xl">{type.name}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="mb-4 text-sm text-muted-foreground line-clamp-2">{type.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center text-sm font-medium text-muted-foreground">
                                <Clock className="mr-1 h-4 w-4" /> {type.duration_minutes} mins
                              </span>
                              <span className="text-lg font-bold text-primary">₹{(type.price_paise / 100).toFixed(0)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {step === "consultant" && (
                  <div>
                    <div className="mb-6 flex items-center justify-between">
                      <h3 className="text-2xl font-semibold">Choose your Consultant</h3>
                      <Button variant="ghost" size="sm" onClick={() => setStep("type")}>Change Type</Button>
                    </div>
                    <div className="grid gap-6">
                      {(consultantsQuery.data || []).map((consultant) => (
                        <Card 
                          key={consultant.id}
                          className={cn(
                            "cursor-pointer transition-all hover:border-primary/50",
                            selectedConsultant?.id === consultant.id && "border-primary bg-primary/5"
                          )}
                          onClick={() => {
                            setSelectedConsultant(consultant);
                            setStep("time");
                          }}
                        >
                          <div className="flex items-start p-6">
                            <div className="mr-4 h-16 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
                              {consultant.image_url ? (
                                <img src={consultant.image_url} alt={consultant.name} className="h-full w-full object-cover" />
                              ) : (
                                <User className="h-full w-full p-4 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold">{consultant.name}</h4>
                              <p className="mt-1 text-sm text-muted-foreground">{consultant.bio}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {step === "time" && (
                  <div>
                    <div className="mb-6 flex items-center justify-between">
                      <h3 className="text-2xl font-semibold">Select Time Slot</h3>
                      <Button variant="ghost" size="sm" onClick={() => setStep("consultant")}>Change Consultant</Button>
                    </div>
                    
                    {availabilityQuery.isLoading ? (
                      <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : availabilityQuery.error ? (
                      <div className="text-destructive p-4">Error loading availability: {(availabilityQuery.error as any).message}</div>
                    ) : availableSlots.length > 0 ? (

                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                        {availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            variant={selectedSlot === slot ? "default" : "outline"}
                            className={cn("h-auto py-3 px-2 flex flex-col items-center")}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            <span className="text-xs opacity-70">
                              {new Date(slot).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </span>
                            <span className="font-bold">
                              {new Date(slot).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </span>
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-12 text-center">
                        <Calendar className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-20" />
                        <p className="text-muted-foreground">No available slots found for this consultant.</p>
                      </div>
                    )}

                  </div>
                )}
              </div>

              {/* Summary Sidebar */}
              <div className="space-y-6">
                <Card className="sticky top-24 bg-muted/30">
                  <CardHeader>
                    <CardTitle className="text-lg">Booking Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedType && (
                      <div className="flex justify-between border-b pb-2 text-sm">
                        <span className="text-muted-foreground">Type:</span>
                        <span className="font-medium">{selectedType.name}</span>
                      </div>
                    )}
                    {selectedConsultant && (
                      <div className="flex justify-between border-b pb-2 text-sm">
                        <span className="text-muted-foreground">Consultant:</span>
                        <span className="font-medium">{selectedConsultant.name}</span>
                      </div>
                    )}
                    {selectedSlot && (
                      <div className="flex justify-between border-b pb-2 text-sm">
                        <span className="text-muted-foreground">Time:</span>
                        <span className="font-medium text-right">{formatISTDateTime(selectedSlot)}</span>
                      </div>
                    )}
                    <div className="pt-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span className="text-primary">₹{selectedType ? (selectedType.price_paise / 100).toFixed(0) : "0"}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground italic text-right">Includes all taxes</p>
                    </div>

                    {!selectedSlot && step !== "type" && (
                      <div className="mt-4 flex items-start gap-2 rounded-md bg-blue-50 p-3 text-xs text-blue-800">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <p>Slot availability is checked in real-time. Please select a time to proceed.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {step === "time" && (
                  <div><p className="mb-3 text-sm text-muted-foreground">Consultation payments are temporarily unavailable. Please contact us to arrange your consultation.</p><Button 
                    size="lg" 
                    disabled
                    onClick={handleBooking}
                    className="w-full"
                  >
                    {isBooking ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</> : "Proceed to Checkout"}
                  </Button></div>
                )}
              </div>
            </div>
          )}
        </Container>
      </Section>
    </SiteLayout>
  );
}
